import { Branch } from "./useExcludedBranch";
import {
	branchKey,
	isExpandable,
	nodeAtBranch,
	pathFromRoot,
} from "./hierarchyTree";
import type { ValueRow } from "./valueRow";
import QuickFilterNodeDTO = App.Data.Explorer.Filter.QuickFilterNodeDTO;

export type HierarchyRow = ValueRow & {
	branches: Branch[];
};

export type NodeWithBranch = QuickFilterNodeDTO & { branch: Branch };

const harvest = (
	node: QuickFilterNodeDTO,
	branch: Branch,
	remaining: number,
	isPruned: (branch: Branch) => boolean,
	out: NodeWithBranch[]
): void => {
	(node.children ?? []).forEach((child, index) => {
		const childBranch = [...branch, index];

		if (remaining <= 1) {
			out.push({ ...child, branch: childBranch });

			return;
		}

		if (isPruned(childBranch)) {
			return;
		}

		harvest(child, childBranch, remaining - 1, isPruned, out);
	});
};

const ancestorLabels = (
	root: QuickFilterNodeDTO | null,
	scopeBranch: Branch,
	branch: Branch
): string[] =>
	pathFromRoot(root, branch)
		.slice(scopeBranch.length + 1, -1)
		.map((node) => node.visibleLabel);

export const buildLevelRows = (
	root: QuickFilterNodeDTO | null,
	scopeBranch: Branch,
	depth: number,
	{
		isPruned = () => false,
	}: {
		isPruned?: (branch: Branch) => boolean;
	} = {}
): HierarchyRow[] => {
	const scope = nodeAtBranch(root, scopeBranch);

	if (!scope) {
		return [];
	}

	const harvested: NodeWithBranch[] = [];
	harvest(scope, scopeBranch, Math.max(depth, 1), isPruned, harvested);

	const rows = new Map<string, HierarchyRow>();

	harvested.forEach((node) => {
		const identity = `${node.label}_${node.level}`;
		const parents = ancestorLabels(root, scopeBranch, node.branch);
		const existing = rows.get(identity);

		if (existing) {
			existing.branches.push(node.branch);

			if (parents.length > 0) {
				existing.parentPaths = [...(existing.parentPaths ?? []), parents];
			}

			existing.canDrill = false;

			return;
		}

		rows.set(identity, {
			key: branchKey(node.branch),
			label: node.visibleLabel,
			isAsset: node.isAsset,
			parentPaths: parents.length > 0 ? [parents] : [],

			canDrill: !scope.isAsset && (node.isAsset || isExpandable(node)),
			branches: [node.branch],
		});
	});

	return [...rows.values()];
};

export const primaryBranch = (row: HierarchyRow): Branch => row.branches[0];
